import { forwardRef } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import type { BaseInputProps } from './types';
import { useBaseFieldControl } from './internal/baseFieldContext';
import { resolveBaseAppearance } from './theme';
import { cn, hasRenderableNode } from './utils';

const controlSizeClassNames = {
  sm: 'h-8 px-2.5 text-xs',
  md: 'h-9 px-3 text-sm',
  lg: 'h-10 px-3.5 text-base',
} as const;

export const BaseInput = forwardRef<HTMLInputElement, BaseInputProps>(
  function BaseInput(
    {
      icon,
      trailing,
      invalid = false,
      disabled = false,
      readOnly = false,
      controlSize,
      appearance,
      className,
      classNames,
      style,
      'aria-invalid': nativeAriaInvalid,
      onBlur,
      onChange,
      ...inputProps
    },
    ref,
  ) {
    for (const [name, value] of [
      ['invalid', invalid],
      ['disabled', disabled],
      ['readOnly', readOnly],
    ] as const) {
      if (typeof value !== 'boolean') {
        throw new TypeError(`BaseInput ${name} must be a boolean.`);
      }
    }
    if (
      controlSize !== undefined &&
      controlSize !== 'sm' &&
      controlSize !== 'md' &&
      controlSize !== 'lg'
    ) {
      throw new TypeError('BaseInput controlSize must be "sm", "md", or "lg".');
    }

    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);
    const fieldControl = useBaseFieldControl();
    const resolvedInvalid = invalid || fieldControl.invalid;

    return (
      <div
        {...componentAttributes}
        data-monkeys-component="input"
        data-slot="root"
        data-size={controlSize}
        data-invalid={resolvedInvalid || undefined}
        data-disabled={disabled || undefined}
        data-readonly={readOnly || undefined}
        className={cn(
          theme.slots.controlWrapper,
          resolvedInvalid && theme.slots.controlInvalid,
          disabled && theme.slots.controlDisabled,
          readOnly && theme.slots.controlReadOnly,
          classNames?.root,
          className,
        )}
        style={{
          ...theme.styles.control,
          ...(resolvedInvalid ? theme.styles.controlInvalid : undefined),
          ...(disabled ? theme.styles.controlDisabled : undefined),
          ...style,
        }}
      >
        {hasRenderableNode(icon) ? (
          <span
            data-slot="icon"
            className={cn(theme.slots.controlIcon, classNames?.icon)}
          >
            {icon}
          </span>
        ) : null}
        <input
          {...inputProps}
          ref={ref}
          disabled={disabled}
          readOnly={readOnly}
          aria-invalid={nativeAriaInvalid ?? (resolvedInvalid || undefined)}
          data-slot="control"
          data-size={controlSize}
          data-invalid={resolvedInvalid || undefined}
          data-disabled={disabled || undefined}
          data-readonly={readOnly || undefined}
          className={cn(
            theme.slots.controlBase,
            theme.slots.controlInput,
            controlSize && controlSizeClassNames[controlSize],
            classNames?.control,
          )}
          onBlur={(event) => {
            onBlur?.(event);
            fieldControl.notifyBlur(event.currentTarget.value);
          }}
          onChange={(event) => {
            onChange?.(event);
            fieldControl.notifyChange(event.currentTarget.value);
          }}
        />
        {hasRenderableNode(trailing) ? (
          <span
            data-slot="trailing"
            className={cn(theme.slots.controlTrailing, classNames?.trailing)}
          >
            {trailing}
          </span>
        ) : null}
      </div>
    );
  },
);
